import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import {
  ArrowLeftIcon,
  BuildingOfficeIcon,
  PhoneIcon,
  MapPinIcon,
  DocumentTextIcon,
  CurrencyDollarIcon,
  WrenchScrewdriverIcon,
  UserCircleIcon,
  KeyIcon,
  ExclamationTriangleIcon,
  PhotoIcon,
  PaperClipIcon,
  XMarkIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';
import useAuthStore from '../store/authStore';

const EditProject = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    title: '',
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    construction_types: '',
    address: '',
    description: '',
    estimated_amount: '',
    contract_amount: '',
    contractor_id: '',
    status: '',
    is_urgent: false,
    customer_password: ''
  });
  
  // 알림 발송 옵션 상태
  const [notificationOptions, setNotificationOptions] = useState({
    sendNotification: false, // 기본값을 false로 변경
    notificationTarget: 'all', // all, customer, contractor
    notificationChannels: {
      sms: true,
      email: true,
      push: true
    }
  });
  
  // 소장 배정 알림 옵션 (별도 관리)
  const [sendContractorNotification, setSendContractorNotification] = useState(false);

  const [selectedTypes, setSelectedTypes] = useState([]);
  const [customType, setCustomType] = useState('');
  const [isCustomType, setIsCustomType] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [deletedFileIds, setDeletedFileIds] = useState([]);
  const [existingFiles, setExistingFiles] = useState([]);

  // 프로젝트 데이터 조회
  const { data: project, isLoading: projectLoading, error: projectError } = useQuery(
    ['project', id],
    () => api.get(`/projects/${id}`).then(res => res.data),
    {
      enabled: !!id, // ID가 있을 때만 쿼리 실행
      refetchOnMount: 'always', // 컴포넌트 마운트 시 항상 새로 가져오기
      refetchOnWindowFocus: false, // 창 포커스 시 재조회 비활성화
      staleTime: 0, // 캐시를 즉시 stale로 표시
      cacheTime: 5 * 60 * 1000, // 5분 캐시
      retry: 1, // 실패 시 1번만 재시도
      onSuccess: (data) => {
        setFormData({
          title: data.title || '',
          customer_name: data.customer?.name || '',
          customer_phone: data.customer?.phone || '',
          customer_email: data.customer?.email || '',
          construction_types: data.construction_types || '',
          address: data.address || '',
          description: data.description || '',
          estimated_amount: data.estimated_amount || '',
          contract_amount: data.contract_amount || '',
          contractor_id: data.contractor_id || '',
          status: data.status || 'received',
          is_urgent: data.is_urgent || false,
          customer_password: data.customer_password || ''
        });
        
        // 공사 유형이 콤마로 구분된 경우 배열로 변환
        if (data.construction_types && data.construction_types.includes(',')) {
          setSelectedTypes(data.construction_types.split(',').map(type => type.trim()));
        } else if (data.construction_types) {
          setSelectedTypes([data.construction_types]);
        }
        
        // 기존 프로젝트 파일 설정
        if (data.ProjectFiles) {
          setExistingFiles(data.ProjectFiles.filter(file => file.category === 'initial' && !file.is_deleted));
        }
      },
      onError: (error) => {
        console.error('프로젝트 데이터 로드 실패:', error);
        toast.error('프로젝트 정보를 불러오는데 실패했습니다.');
      }
    }
  );

  // 설정에서 공사 유형 가져오기
  const { data: settings } = useQuery(
    'admin-settings',
    () => api.get('/admin/settings').then(res => res.data),
    {
      refetchOnMount: 'always',
      staleTime: 0,
      cacheTime: 0,
      retry: 1
    }
  );

  // 소장 목록 가져오기
  const { data: contractorsData } = useQuery(
    'admin-contractors',
    () => api.get('/admin/contractors').then(res => res.data),
    {
      refetchOnMount: 'always',
      staleTime: 0,
      retry: 1
    }
  );
  
  const contractors = contractorsData?.contractors || [];

  const constructionTypes = settings?.construction_types?.split(',').map(type => type.trim()) || [];

  // 프로젝트 수정 mutation
  const updateMutation = useMutation(
    (data) => {
      const formData = new FormData();
      
      // 프로젝트 데이터 추가
      Object.keys(data).forEach(key => {
        if (data[key] !== null && data[key] !== undefined && key !== 'files' && key !== 'deleted_files' && key !== 'notification_channels') {
          formData.append(key, data[key]);
        }
      });
      
      // 알림 채널 추가 (배열을 JSON 문자열로 변환)
      if (data.notification_channels && data.notification_channels.length > 0) {
        formData.append('notification_channels', JSON.stringify(data.notification_channels));
      }
      
      // 삭제할 파일 ID 추가
      if (deletedFileIds.length > 0) {
        formData.append('deleted_files', JSON.stringify(deletedFileIds));
      }
      
      // 새로운 파일 추가
      selectedFiles.forEach(file => {
        formData.append('files', file);
      });
      
      return api.put(`/projects/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    },
    {
      onSuccess: () => {
        toast.success('프로젝트가 수정되었습니다.');
        queryClient.invalidateQueries(['project', id]);
        queryClient.invalidateQueries('admin-projects');
        queryClient.invalidateQueries('admin-customers'); // 고객 정보 변경 시 목록도 새로고침
        navigate('/admin/projects');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || '프로젝트 수정에 실패했습니다.');
      }
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const updatedData = {
      ...formData,
      construction_types: isCustomType ? customType : selectedTypes[0] || '',
      estimated_amount: formData.estimated_amount || null,
      contract_amount: formData.contract_amount || null,
      contractor_id: formData.contractor_id || null,
      customer_email: formData.customer_email || null,
      // 알림 옵션 추가
      send_notification: notificationOptions.sendNotification,
      notification_target: notificationOptions.notificationTarget,
      notification_channels: notificationOptions.sendNotification ? 
        Object.keys(notificationOptions.notificationChannels)
          .filter(channel => notificationOptions.notificationChannels[channel]) : [],
      // 소장 배정 알림 옵션
      send_contractor_notification: sendContractorNotification
    };

    updateMutation.mutate(updatedData);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  // 파일 선택 처리
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const totalSize = [...selectedFiles, ...files].reduce((sum, file) => sum + file.size, 0);
    
    if (totalSize > 50 * 1024 * 1024) { // 50MB 제한
      toast.error('전체 파일 크기는 50MB를 초과할 수 없습니다.');
      return;
    }
    
    setSelectedFiles(prev => [...prev, ...files]);
    e.target.value = null; // 같은 파일 재선택 가능하도록
  };
  
  // 새 파일 제거
  const removeNewFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };
  
  // 기존 파일 삭제 표시
  const markFileForDeletion = (fileId) => {
    setDeletedFileIds(prev => [...prev, fileId]);
    setExistingFiles(prev => prev.filter(file => file.id !== fileId));
  };
  
  // 파일 크기 포맷
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };
  
  // 파일 아이콘 가져오기
  const getFileIcon = (file) => {
    const ext = (file.name || file.file_name).split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) {
      return <PhotoIcon className="h-5 w-5 text-blue-500" />;
    }
    return <DocumentTextIcon className="h-5 w-5 text-gray-500" />;
  };

  if (projectLoading || !project) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">프로젝트 정보를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (projectError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">프로젝트 정보를 불러오는데 실패했습니다.</p>
          <button
            onClick={() => window.location.reload()}
            className="btn-primary"
          >
            새로고침
          </button>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-600">프로젝트를 찾을 수 없습니다.</p>
          <button
            onClick={() => navigate('/admin/projects')}
            className="mt-4 text-indigo-600 hover:text-indigo-500"
          >
            프로젝트 목록으로 돌아가기
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 헤더 */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/admin/projects')}
                className="mr-4 p-2 rounded-lg hover:bg-gray-100"
              >
                <ArrowLeftIcon className="h-5 w-5" />
              </button>
              <h1 className="text-xl font-semibold text-gray-900">프로젝트 수정</h1>
            </div>
          </div>
        </div>
      </div>

      {/* 폼 */}
      <div className="max-w-3xl mx-auto px-3 sm:px-4 py-4 sm:py-8">
        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
          {/* 기본 정보 */}
          <div className="bg-white shadow rounded-lg p-4 sm:p-6">
            <h2 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4">기본 정보</h2>
            
            <div className="grid grid-cols-1 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  프로젝트명 *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  required
                  className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  공사 유형 *
                </label>
                {!isCustomType ? (
                  <div className="space-y-2">
                    <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded-md p-3">
                      <p className="text-xs text-gray-500 mb-2">1개만 선택 가능합니다</p>
                      {constructionTypes.map((type) => (
                        <label key={type} className="flex items-center">
                          <input
                            type="radio"
                            name="construction_type"
                            value={type}
                            checked={selectedTypes.includes(type)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedTypes([type]);
                              }
                            }}
                            className="h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                          />
                          <span className="ml-2 text-sm text-gray-700">{type}</span>
                        </label>
                      ))}
                    </div>
                    {selectedTypes.length > 0 && (
                      <div className="text-sm text-gray-600">
                        선택됨: {selectedTypes[0]}
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => setIsCustomType(true)}
                      className="text-sm text-indigo-600 hover:text-indigo-500"
                    >
                      직접 입력
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={customType}
                      onChange={(e) => setCustomType(e.target.value)}
                      placeholder="공사 유형을 입력하세요"
                      className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setIsCustomType(false);
                        setCustomType('');
                      }}
                      className="text-sm text-gray-600 hover:text-gray-500"
                    >
                      목록에서 선택
                    </button>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  공사 주소
                </label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  상세 설명
                </label>
                <textarea
                  rows={4}
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>
          </div>

          {/* 고객 정보 */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">고객 정보</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  고객명 *
                </label>
                <input
                  type="text"
                  value={formData.customer_name}
                  onChange={(e) => handleChange('customer_name', e.target.value)}
                  required
                  className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  연락처 *
                </label>
                <input
                  type="tel"
                  value={formData.customer_phone}
                  onChange={(e) => handleChange('customer_phone', e.target.value)}
                  required
                  className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  이메일
                </label>
                <input
                  type="email"
                  value={formData.customer_email}
                  onChange={(e) => handleChange('customer_email', e.target.value)}
                  className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  고객 비밀번호
                </label>
                <input
                  type="text"
                  value={formData.customer_password}
                  onChange={(e) => handleChange('customer_password', e.target.value)}
                  className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="URL 접속용 비밀번호"
                />
              </div>
            </div>
          </div>

          {/* 프로젝트 관리 */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">프로젝트 관리</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  담당 소장
                </label>
                <select
                  value={formData.contractor_id}
                  onChange={(e) => handleChange('contractor_id', e.target.value)}
                  className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">미배정</option>
                  {contractors?.map((contractor) => (
                    <option key={contractor.id} value={contractor.id}>
                      {contractor.name} ({contractor.company_name})
                    </option>
                  ))}
                </select>
                {/* 소장 배정 알림 옵션 - 항상 표시 */}
                <label className="flex items-center mt-2">
                  <input
                    type="checkbox"
                    checked={sendContractorNotification}
                    onChange={(e) => setSendContractorNotification(e.target.checked)}
                    className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  />
                  <span className="ml-2 text-sm text-gray-600">소장 배정 알림 발송</span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  프로젝트 상태
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => handleChange('status', e.target.value)}
                  className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="received">접수</option>
                  <option value="review">검토</option>
                  <option value="contract">계약</option>
                  <option value="completed">완료</option>
                  <option value="cancelled">취소</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  예상 금액
                </label>
                <input
                  type="number"
                  value={formData.estimated_amount}
                  onChange={(e) => handleChange('estimated_amount', e.target.value)}
                  className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  계약 금액
                </label>
                <input
                  type="number"
                  value={formData.contract_amount}
                  onChange={(e) => handleChange('contract_amount', e.target.value)}
                  className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="0"
                />
              </div>

              <div className="md:col-span-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_urgent}
                    onChange={(e) => handleChange('is_urgent', e.target.checked)}
                    className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">긴급 프로젝트</span>
                </label>
              </div>
            </div>
          </div>
          
          {/* 알림 발송 설정 */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">알림 발송 설정</h2>
            <div className="space-y-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={notificationOptions.sendNotification}
                  onChange={(e) => setNotificationOptions(prev => ({
                    ...prev,
                    sendNotification: e.target.checked
                  }))}
                  className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                />
                <span className="ml-2 text-sm font-medium text-gray-700">변경사항 알림 발송</span>
              </label>
              
              {notificationOptions.sendNotification && (
                <>
                  {/* 발송 대상 선택 */}
                  <div>
                    <label className="text-sm font-medium text-gray-700">발송 대상</label>
                    <div className="mt-2 space-y-2">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="notificationTarget"
                          value="all"
                          checked={notificationOptions.notificationTarget === 'all'}
                          onChange={(e) => setNotificationOptions(prev => ({
                            ...prev,
                            notificationTarget: e.target.value
                          }))}
                          className="h-4 w-4 text-indigo-600"
                        />
                        <span className="ml-2 text-sm text-gray-700">모두에게 발송</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="notificationTarget"
                          value="customer"
                          checked={notificationOptions.notificationTarget === 'customer'}
                          onChange={(e) => setNotificationOptions(prev => ({
                            ...prev,
                            notificationTarget: e.target.value
                          }))}
                          className="h-4 w-4 text-indigo-600"
                        />
                        <span className="ml-2 text-sm text-gray-700">고객에게만 발송</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="notificationTarget"
                          value="contractor"
                          checked={notificationOptions.notificationTarget === 'contractor'}
                          onChange={(e) => setNotificationOptions(prev => ({
                            ...prev,
                            notificationTarget: e.target.value
                          }))}
                          className="h-4 w-4 text-indigo-600"
                        />
                        <span className="ml-2 text-sm text-gray-700">소장에게만 발송</span>
                      </label>
                    </div>
                  </div>
                  
                  {/* 발송 채널 선택 */}
                  <div>
                    <label className="text-sm font-medium text-gray-700">발송 채널</label>
                    <div className="mt-2 flex space-x-4">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={notificationOptions.notificationChannels.sms}
                          onChange={(e) => setNotificationOptions(prev => ({
                            ...prev,
                            notificationChannels: {
                              ...prev.notificationChannels,
                              sms: e.target.checked
                            }
                          }))}
                          className="h-4 w-4 text-indigo-600 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">SMS</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={notificationOptions.notificationChannels.email}
                          onChange={(e) => setNotificationOptions(prev => ({
                            ...prev,
                            notificationChannels: {
                              ...prev.notificationChannels,
                              email: e.target.checked
                            }
                          }))}
                          className="h-4 w-4 text-indigo-600 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">이메일</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={notificationOptions.notificationChannels.push}
                          onChange={(e) => setNotificationOptions(prev => ({
                            ...prev,
                            notificationChannels: {
                              ...prev.notificationChannels,
                              push: e.target.checked
                            }
                          }))}
                          className="h-4 w-4 text-indigo-600 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">푸시</span>
                      </label>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
          
          {/* 첨부파일 관리 */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">첨부파일</h2>
            
            {/* 기존 파일 목록 */}
            {existingFiles.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-700 mb-3">기존 첨부파일</h3>
                <div className="space-y-2">
                  {existingFiles.map((file) => (
                    <div key={file.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        {getFileIcon(file)}
                        <div>
                          <p className="text-sm font-medium text-gray-900">{file.file_name}</p>
                          <p className="text-xs text-gray-500">{formatFileSize(file.file_size)}</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => markFileForDeletion(file.id)}
                        className="text-red-600 hover:text-red-800"
                        title="삭제"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* 파일 업로드 영역 */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">새 파일 추가</h3>
              <div 
                className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                <PaperClipIcon className="mx-auto h-12 w-12 text-gray-400" />
                <p className="mt-2 text-sm text-gray-600">
                  클릭하여 파일을 선택하거나 여기에 파일을 드래그하세요
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  이미지 및 문서 파일 (최대 50MB)
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.hwp,.hwpx,.zip"
                />
              </div>
              
              {/* 선택된 파일 목록 */}
              {selectedFiles.length > 0 && (
                <div className="mt-4 space-y-2">
                  <p className="text-sm font-medium text-gray-700">
                    선택된 파일 ({selectedFiles.length}개)
                  </p>
                  <div className="space-y-2">
                    {selectedFiles.map((file, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          {getFileIcon(file)}
                          <div>
                            <p className="text-sm font-medium text-gray-900">{file.name}</p>
                            <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeNewFile(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <XMarkIcon className="h-5 w-5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* 버튼 */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => navigate('/admin/projects')}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              취소
            </button>
            <button
              type="submit"
              disabled={updateMutation.isLoading}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
            >
              {updateMutation.isLoading ? '수정 중...' : '수정하기'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProject;